import type { DraftExperiment, DraftExperimentUpdateBody } from '../types/experiment-draft.types';

/** Suffix appended to the source name so a restarted run is distinguishable in the listing. */
export const RESTART_NAME_SUFFIX = ' (Restart)';

/** Append the restart suffix to a source name, guarding against a null/blank name. */
export function restartName(sourceName: string | null | undefined): string | null {
  const base = sourceName?.trim() ?? '';
  return base ? `${base}${RESTART_NAME_SUFFIX}` : null;
}

/**
 * Build the PATCH body that clones a source experiment's configuration onto a
 * freshly-created restart draft. Every run-defining field (scope, grouping,
 * arms, KPIs, duration, content attributes incl. per-arm, catalog snapshot) is copied
 * verbatim for parity; the only change is the distinguishing name suffix.
 * `stage` is set to `REVIEW_AND_LAUNCH` so the clone is launch-ready. Optional
 * wire fields are spread conditionally so a missing source value is never sent
 * as `undefined` (rejected under `exactOptionalPropertyTypes`).
 */
export function mapSourceToRestartUpdateBody(source: DraftExperiment): DraftExperimentUpdateBody {
  return {
    name: restartName(source.name),
    primaryKpi: source.primaryKpi,
    secondaryKpi: source.secondaryKpi,
    numberOfArms: source.numberOfArms,
    ...(source.arms ? { arms: source.arms } : {}),
    ...(source.trafficSplit ? { trafficSplit: source.trafficSplit } : {}),
    testDurationDays: source.testDurationDays,
    contentAttributes: source.contentAttributes,
    // Carry the per-arm selection forward; a single-selection source has none.
    ...(source.armContentAttributes ? { armContentAttributes: source.armContentAttributes } : {}),
    parentIdField: source.parentIdField,
    ...(source.scopingFilters ? { scopingFilters: source.scopingFilters } : {}),
    // `null` persists "no grouping"; an object clones the custom reporting segment.
    groupingFilter: source.groupingFilter
      ? { name: source.groupingFilter.name, filters: source.groupingFilter.filters }
      : null,
    ...(source.skuCount != null ? { skuCount: source.skuCount } : {}),
    ...(source.syncId ? { syncId: source.syncId } : {}),
    stage: 'REVIEW_AND_LAUNCH',
  };
}
